/** Strip diacritics so "José" / "Jose" and similar names can match. */
export function removeDiacritics(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .normalize("NFC");
}

function normalizeForAdminSearch(value: string): string {
  return removeDiacritics(value).trim().toLowerCase();
}

/** Exact (case-insensitive) or broad (accent-insensitive) substring match. */
export function containsExactOrBroad(
  haystack: string | null | undefined,
  needle: string,
): boolean {
  const term = needle.trim();
  if (!term) return true;
  const field = haystack ?? "";
  if (field.toLowerCase().includes(term.toLowerCase())) return true;
  return normalizeForAdminSearch(field).includes(normalizeForAdminSearch(term));
}

type AdminSearchableUser = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

/**
 * Admin user search: exact case-insensitive match OR accent-insensitive (broad) match
 * on first name, last name, email, and full name. Multi-token queries require every
 * token to match at least one field (e.g. "José García").
 */
export function matchesAdminUserSearch(
  user: AdminSearchableUser,
  searchTerm: string,
): boolean {
  const term = searchTerm.trim();
  if (!term) return true;

  const first = user.firstName ?? "";
  const last = user.lastName ?? "";
  const email = user.email ?? "";
  const fullName = `${first} ${last}`.trim();

  if (
    containsExactOrBroad(first, term) ||
    containsExactOrBroad(last, term) ||
    containsExactOrBroad(email, term) ||
    containsExactOrBroad(fullName, term)
  ) {
    return true;
  }

  const tokens = term.split(/\s+/).filter(Boolean);
  if (tokens.length <= 1) return false;

  return tokens.every(
    (token) =>
      containsExactOrBroad(first, token) ||
      containsExactOrBroad(last, token) ||
      containsExactOrBroad(email, token) ||
      containsExactOrBroad(fullName, token),
  );
}
